import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { GuideService } from '../../../services/resources/guide.service';
import { LanguageMap } from '../../../core/languages';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { first } from 'rxjs';
import { GuideDetailInterface } from '../../../core/models';
import {
  CountryISO,
  NgxIntlTelInputModule,
  SearchCountryField,
} from 'ngx-intl-tel-input';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import { BookingService } from '../../../services/resources/booking.service';

@Component({
  selector: 'app-resources-editor',
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    NgxIntlTelInputModule,
  ],
  standalone: true,
  template: `
    <div class="mx-auto max-w-7xl pt-6 px-4 sm:px-6 lg:px-8">
      <form [formGroup]="guideForm" (ngSubmit)="onSubmit()" class="space-y-12">
        <div class="border-b border-gray-900/10 pb-12">
          <h2 class="text-base font-semibold leading-7 text-gray-900">
            Guide Information
          </h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">
            Enter the details for the guide below.
          </p>

          <div class="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label
                for="forename"
                class="block text-sm font-medium leading-6 text-gray-900"
                >Forename</label
              >
              <input
                id="forename"
                type="text"
                formControlName="forename"
                class="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-columbus-blue-600 sm:text-sm sm:leading-6"
              />
            </div>

            <div class="sm:col-span-3">
              <label
                for="surname"
                class="block text-sm font-medium leading-6 text-gray-900"
                >Surname</label
              >
              <input
                id="surname"
                type="text"
                formControlName="surname"
                class="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-columbus-blue-600 sm:text-sm sm:leading-6"
              />
            </div>

            <div class="sm:col-span-3">
              <label
                for="tel"
                class="block text-sm font-medium leading-6 text-gray-900"
                >Phone Number</label
              >
              <ngx-intl-tel-input
                [cssClass]="
                  'mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-columbus-blue-600 sm:text-sm sm:leading-6'
                "
                [preferredCountries]="preferredCountries"
                [enableAutoCountrySelect]="true"
                [enablePlaceholder]="true"
                [searchCountryFlag]="true"
                [searchCountryField]="[
                  SearchCountryField.Iso2,
                  SearchCountryField.Name,
                ]"
                [selectFirstCountry]="false"
                [selectedCountryISO]="countryCode"
                [phoneValidation]="true"
                [separateDialCode]="true"
                formControlName="tel"
              ></ngx-intl-tel-input>
              <div
                *ngIf="
                  guideForm.get('tel')?.invalid && guideForm.get('tel')?.touched
                "
                class="text-red-500 text-sm mt-1"
              >
                Valid phone number is required.
              </div>
            </div>
            <div class="sm:col-span-3">
              <label
                for="email"
                class="block text-sm font-medium leading-6 text-gray-900"
                >Email Address</label
              >
              <input
                id="email"
                type="email"
                formControlName="email"
                class="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-columbus-blue-600 sm:text-sm sm:leading-6"
              />
              <div
                *ngIf="
                  guideForm.get('email')?.invalid &&
                  guideForm.get('email')?.touched
                "
                class="text-red-500 text-sm mt-1"
              >
                Valid email is required.
              </div>
            </div>
          </div>

          <div class="space-y-5 py-6">
            <div class="relative flex items-start">
              <div class="flex h-6 items-center">
                <input
                  id="tAndC"
                  aria-describedby="Terms And Conditions"
                  formControlName="tAndC"
                  type="checkbox"
                  class="h-4 w-4 rounded border-gray-300 text-columbus-blue-600 focus:ring-columbus-blue-600"
                />
              </div>
              <div class="ml-3 text-sm leading-6">
                <label for="tAndC" class="font-medium text-gray-900"
                  >Terms and Conditions -
                </label>
                <span id="tAndC-description" class="text-gray-500">
                  Accepted by the Guide
                </span>
              </div>
            </div>
          </div>
          <hr />
          <div class="sm:col-span-3 pt-6">
            <label
              for="languages"
              class="block text-sm font-medium leading-6 text-gray-900"
              >Languages</label
            >
            <div
              [formGroup]="languages"
              class="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-3"
            >
              <div
                *ngFor="
                  let langCode of languages.controls | keyvalue;
                  let i = index
                "
                class="flex items-center"
              >
                <input
                  id="language-{{ i }}"
                  type="checkbox"
                  [formControlName]="langCode.key"
                  class="col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 bg-white checked:border-columbus-blue-600 checked:bg-columbus-blue-600 indeterminate:border-columbus-blue-600 indeterminate:bg-columbus-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100"
                />
                <label
                  for="language-{{ i }}"
                  class="ml-2 text-sm text-gray-900"
                  >{{ languageName(langCode.key) }}</label
                >
              </div>
            </div>
          </div>
        </div>

        <div class="mt-6 flex items-center justify-end gap-x-6">
          <button
            type="button"
            (click)="onCancel()"
            class="text-sm font-semibold leading-6 text-gray-900"
          >
            Cancel
          </button>
          <button
            type="submit"
            [disabled]="guideForm.invalid"
            class="rounded-md bg-columbus-blue-600 disabled:opacity-25 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-columbus-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600"
          >
            Save
          </button>
          <!--
          <button
            type="submit"
            (click)="onMigration()"
            class="rounded-md bg-columbus-blue-600 disabled:opacity-25 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-columbus-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600"
          >
            Migrate
          </button>
          -->
        </div>
      </form>
    </div>
  `,
  styles: ``,
})
export class ResourcesEditorComponent implements OnInit {
  guideForm: FormGroup;
  guideService = inject(GuideService);
  bookingService = inject(BookingService);
  languageOptions = Object.entries(LanguageMap).map(([key, value]) => ({
    code: key,
    name: value,
  }));

  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  preferredCountries: CountryISO[] = [
    CountryISO.Austria,
    CountryISO.Germany,
    CountryISO.Switzerland,
    CountryISO.Hungary,
    CountryISO.Slovakia,
  ];
  countryCode: CountryISO = CountryISO.Austria;
  PhoneNumberFormat = PhoneNumberFormat;
  languageForm: FormGroup;
  id: string | null = null;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.id = this.route.snapshot.paramMap.get('id');

    this.languageForm = this.fb.group({});
    this.languageOptions.forEach((lang) => {
      this.languageForm.addControl(lang.code, new FormControl(false));
    });

    this.guideForm = this.fb.group({
      id: [''],
      forename: ['', Validators.required],
      surname: ['', Validators.required],
      languages: this.languageForm,
      tel: [
        '',
        [
          this.validatePhoneNumber.bind(this), // Bind properly
        ],
      ],
      email: ['', [Validators.required, Validators.email]],
      tAndC: [false],
    });

    if (this.id) {
      this.guideService
        .getGuideData(this.id)
        .pipe(first())
        .subscribe((guide: GuideDetailInterface) => {
          if (guide) {
            const phoneUtil = PhoneNumberUtil.getInstance();
            const number = phoneUtil.parse(guide.tel, 'ZZ');
            const countryC = phoneUtil.getRegionCodeForNumber(
              number,
            ) as CountryISO;
            guide.tel = number.getNationalNumber()?.toString() || '';
            this.countryCode = countryC;
            this.patchForm(guide);
          }
        });
    }
  }

  async onMigration() {
    await this.bookingService.migrateGuideIds();
  }

  patchForm(guide: GuideDetailInterface) {
    this.guideForm.patchValue({
      forename: guide.forename,
      surname: guide.surname,
      id: guide.id,
      tel: guide.tel,
      email: guide.email,
      tAndC: guide.tAndC,
    });

    Object.entries(guide.languages).forEach((lang) => {
      this.languages.get(lang.at(0) as string)?.setValue(lang.at(1) as boolean);
    });
  }
  ngOnInit(): void {}

  languageName(code: string) {
    return this.languageOptions.find((val) => val.code === code)?.name;
  }

  get languages() {
    return this.guideForm.get('languages') as FormGroup;
  }

  onSubmit() {
    if (this.guideForm.valid) {
      let guid: GuideDetailInterface = this.guideForm.getRawValue();
      guid.tAndC = !!guid.tAndC;
      const telValue = this.guideForm.get('tel')?.value;
      guid.tel = telValue?.internationalNumber ?? telValue;
      // Format the phone number
      const phoneUtil = PhoneNumberUtil.getInstance();
      const number = phoneUtil.parse(guid.tel, 'ZZ');
      guid.tel = phoneUtil.format(number, PhoneNumberFormat.INTERNATIONAL);

      guid.languages = Object.fromEntries(
        Object.entries(guid.languages).filter(([, value]) => value),
      );

      if (!this.id) {
        this.guideService.createNewGuide(guid).then(() => {
          this.router.navigateByUrl('/resources');
        });
      } else {
        this.guideService.updateGuide(guid).then(async () => {
          const count =
            await this.bookingService.updateGuideInFutureBookings(guid);
          console.log(`${count} zukünftige Buchungen aktualisiert`);
          this.router.navigateByUrl('/resources');
        });
      }
    } else {
      console.log('Form Invalid');
    }
  }

  onCancel() {
    this.guideForm.reset();
    this.router.navigateByUrl('/resources');
  }

  // Custom validator for phone number
  validatePhoneNumber(control: AbstractControl) {
    const phoneUtil = PhoneNumberUtil.getInstance();
    try {
      const number = phoneUtil.parse(control.value.internationalNumber, 'ZZ'); // 'ZZ' means international format
      return phoneUtil.isValidNumber(number)
        ? null
        : { invalidPhoneNumber: true };
    } catch (e) {
      return { invalidPhoneNumber: true };
    }
  }
}
